<template>
    <div ref="parent" :class="['switcher', { dark, border }]">
        <VfSmartSelect
            class="select"
            v-if="store.isMobile && resolvedOptions.length > 2"
            v-model="localModelValue"
            :options="resolvedOptions"
            label-field="displayText"
            value-field="value"
            :disabled="disabled"
        />

        <button
            v-else
            v-for="(opt, idx) in resolvedOptions"
            :key="idx"
            type="button"
            :class="[{ active: modelValue == opt.value, dark, wide }]"
            :disabled="disabled"
            v-tooltip="tips?.[idx]"
            @click="emit('update:modelValue', opt.value)"
        >
            <i v-if="icons" :class="icons[idx]" />

            <template v-else>
                {{ opt.displayText }}
            </template>
        </button>
    </div>
</template>

<script lang="ts" setup generic="T">
import { VfSmartSelect } from '@zyno-io/vue-foundation';
import { startCase } from 'lodash';
import { computed } from 'vue';

import { useStore } from '@/store';

const store = useStore();

const props = defineProps<{
    modelValue: T | null;
    disabled?: boolean;
    options: T[] | { [K in T extends string ? T : never]?: string };
    icons?: string[];
    tips?: (string | null)[];
    dark?: boolean;
    wide?: boolean;
    border?: boolean;
}>();

const localModelValue = computed({
    get: () => props.modelValue,
    set: (val: T | null) => {
        emit('update:modelValue', val);
    }
});

const emit = defineEmits<(e: 'update:modelValue', val: T | null) => void>();

const resolvedOptions = computed(() => {
    if (Array.isArray(props.options)) {
        return props.options.map(o => ({ value: o, displayText: startCase(String(o)) }));
    }
    // Object keys are always strings, so a `{ false: ..., true: ... }` options map
    // would otherwise emit (and compare against) the strings "false"/"true" instead
    // of booleans. Coerce those two keys back to booleans so a boolean v-model works.
    return Object.entries(props.options).map(([key, displayText]) => {
        const value = key === 'true' ? true : key === 'false' ? false : key;
        return { value: value as T, displayText };
    });
});
</script>

<style scoped>
@reference '#tw.css';

/* Switcher is a small mode/state toggle for one field — visually subordinate
   to tab strips. Active state uses surface + accent-ink text with a 2px cobalt
   underline for affordance, so it reads as a smaller picker rather than a
   primary action. The `dark` variant retains its original heavier treatment
   for sidebar/chrome contexts. */
.switcher {
    @apply flex flex-shrink-0 overflow-hidden;

    > button {
        color: var(--ink-3);
        background: var(--surface);
        border: 1px solid var(--line-2);
        height: var(--ctrl-h);
        padding: 0 12px;
        border-radius: 0;
        transition:
            background 80ms ease,
            color 80ms ease,
            border-color 80ms ease,
            box-shadow 80ms ease;

        &:hover:not(.active):not(:disabled) {
            color: var(--ink);
            background: var(--surface-2);
        }

        /* Drop the left border on subsequent buttons so adjacent buttons share
           a 1px divider instead of stacking to 2px. */
        & + button {
            border-left: 0;
        }

        &:first-of-type {
            @apply rounded-l-md;
        }

        &:last-of-type {
            @apply rounded-r-md;
        }

        &.active {
            color: var(--accent-ink);
            background: var(--surface);
            border-color: var(--line-2);
            box-shadow: inset 0 -2px 0 0 var(--accent);
        }

        /* `dark` variant: lives on `--chrome-panel` (the phone dialer). The
           default v2 retoning would render near-invisible text on dark
           backgrounds, so the dark variant keeps its own legible
           treatment. Selection is carried by an accent fill rather than a
           bg step: the old chrome-2 → chrome-3 shift landed the active
           segment on the same slate-700 as the panel behind it, so which
           option was selected barely read. Unselected segments sit darker
           than the panel so the strip reads as a recessed track. */
        &.dark {
            background: var(--chrome-panel-sunk);
            border-color: var(--chrome-panel-sunk);
            color: var(--ink-on-chrome-2);
            box-shadow: none;
        }

        &.dark:hover:not(.active):not(:disabled) {
            background: var(--chrome-panel);
            color: var(--ink-on-chrome);
        }

        &.dark.active {
            background: var(--accent);
            border-color: var(--accent);
            color: #ffffff;
            font-weight: 600;
            box-shadow: none;
        }

        &.wide {
            @apply flex-1;
        }
    }

    &.dark.border {
        @apply border-chrome-panel-raised rounded-lg;

        > button {
            @apply border-none;
        }
    }
}

.select {
    @apply w-full;
}
</style>
